import { useMemo } from 'react'
import { useAppContext } from '../../../../redux/slices/appContext'
import { useSearchClients } from '../../../../api/clients'

/**
 * Resolves the current `clientId` from app context into a `{ clientId,
 * client, clientName }` triple. `clientName` falls back through
 * longName -> shortName -> null so callers can render a stable label
 * (e.g. "Save View for John Smith"). React Query dedupes the underlying
 * `useSearchClients` call, so multiple consumers on the same page share
 * one network round-trip.
 *
 * When no client is in scope, returns `{ clientId: null, client: null,
 * clientName: null }` and the underlying query stays disabled.
 */
export function useCurrentClient () {
  const { clientId } = useAppContext()

  const clientQuery = useMemo(() => ({
    filters: { clientId: clientId ? [clientId] : [] },
    includes: { avatars: true },
    take: 1
  }), [clientId])

  const { data: clientData } = useSearchClients(clientQuery, {
    enabled: !!clientId
  })

  const client = useMemo(
    () => (clientData?.data || []).at(0) || null,
    [clientData]
  )

  const clientName = client?.longName || client?.shortName || null

  return { clientId, client, clientName }
}
